import React, { useState, useEffect, useMemo, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation, matchPath } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Drawer } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import dayjs from 'dayjs';
import { LiveBookingsHeader } from '@eatclubapp/ec-bookings-library';
import { Box } from '@eatclubapp/ec-component-library';
import AppHeader from '../components/layout/appHeader/AppHeader';
import CustomHeader from '../components/layout/appHeader/CustomHeader';
import ForecastingHeader from '../components/layout/appHeader/ForecastingHeader';
import NavigationDrawer from '../components/layout/navigation/NavigationDrawer';
import { NavigationItemIcon } from '../components/layout/navigation/navigationItemIcons';
import AudienceTargetingModal from '../screens/audienceTargeting/components/AudienceTargetingModal';
import HolidayHoursModal from '../components/offers/Modals/HolidayHoursModal';
import { restaurantActivePropTypes } from '../data/models/Restaurant';
import routes from '../routes/Routes';
import { devLog } from '../utils';
import { isRestaurantDisabled, isVenueInactiveForPromoModals } from '../utils/helpers';
import { canEditAll, FEATURE_SCOPES, hasAccess } from '../utils/permissions';
import useStyles from './AppLayoutStyles';
import { isEmpty } from '../utils/baseHelpers';
import { getAppConfig, getRestaurantConfig } from '../constants/appConfig';
import {
  getHolidayHoursProfileForRestaurant,
  isHolidayHoursSuppressedByConfig,
} from '../utils/holidayHoursProfile';
import { PartnerPromoModalsContext } from '../context/PartnerPromoModalsContext';
import { HolidayHoursNavigationGuardProvider } from '../context/HolidayHoursNavigationGuardContext';
import { NavigationContext } from '../utils/navigationContext';
import { fetchAudienceTargetingAction } from '../actions/audienceTargetingAction';

const HAS_SEEN_AUDIENCE_TARGETING_MODAL = 'hasSeenAudienceTargetingModalInsights';

// TODO this and AppHeader have both become crazy over time with lots of interconnected logic, especially for the nav functionality and banners. Need to refactor when possible
const AppLayout = ({
  platform = '',
  goalsEnabled = true,
  obeeEnabled = false,
  premium = false,
  children,
  ecPayEnabled = false,
  forecasterEnabled = false,
  analyticsEnabled = false,
  restId = null,
  restaurantActive,
  restaurantsFetching = true,
  user,
  audienceTargeting,
  fetchAudienceTargeting,
  holidayScheduleStatus = { fetching: false, success: false, data: { holidaysScheduled: false } },
}) => {
  const appConfig = getAppConfig();
  const location = useLocation();
  const classes = useStyles();
  const theme = useTheme();

  // TODO needs a better loading state now that it's dynamic

  const getNavigationItems = () => {
    const bookingsSlug = obeeEnabled
      ? premium
        ? 'livebookings/floorplan'
        : 'livebookings/runsheet'
      : 'livebookings';

    const billingSlug = ecPayEnabled ? 'settlements' : 'billing';

    const newNavigationItems = [
      ...(analyticsEnabled
        ? [
            {
              id: 'business-analytics',
              name: 'Business analytics',
              slug: 'analytics',
              column: 'management',
              section: 1,
              isBeta: true,
              icon: <NavigationItemIcon id='business-analytics' />,
              allowedScopes: FEATURE_SCOPES.INSIGHTS,
            },
          ]
        : []),
      ...(forecasterEnabled
        ? [
            {
              id: 'forecast',
              name: 'Forecast',
              slug: 'forecast',
              column: 'management',
              section: 1,
              isBeta: true,
              icon: <NavigationItemIcon id='forecast' />,
              allowedScopes: FEATURE_SCOPES.INSIGHTS,
            },
          ]
        : []),
      {
        id: 'offer-performance',
        name: 'Offer performance',
        slug: 'insights',
        column: 'management',
        section: 1,
        allowedScopes: FEATURE_SCOPES.INSIGHTS,
        icon: <NavigationItemIcon id='offer-performance' />,
        activePathPrefixes: ['/insights'],
      },
      {
        id: 'bookings',
        name: 'Bookings',
        slug: bookingsSlug,
        column: 'management',
        section: 1,
        allowedScopes: FEATURE_SCOPES.BOOKINGS,
        icon: <NavigationItemIcon id='bookings' />,
      },
      {
        id: 'create-offer',
        name: 'Create offer',
        slug: 'offers/create',
        column: 'demand',
        section: 1,
        variant: 'create-offer',
        allowedScopes: FEATURE_SCOPES.OFFERS,
      },
      {
        id: 'manage-offers',
        name: 'Manage offers',
        slug: 'offers',
        column: 'demand',
        section: 1,
        icon: <NavigationItemIcon id='manage-offers' />,
      },
      {
        id: 'audience-targeting',
        name: 'Audience targeting',
        slug: 'audience-targeting',
        column: 'demand',
        section: 1,
        allowedScopes: FEATURE_SCOPES.AUDIENCE_TARGETING,
        icon: <NavigationItemIcon id='audience-targeting' />,
        activePathPrefixes: ['/audience-targeting', '/audiences-networks'],
      },
      {
        id: 'guest-manager',
        name: 'Guest manager',
        slug: 'guest-manager',
        column: 'demand',
        section: 1,
        allowedScopes: FEATURE_SCOPES.BOOKINGS,
        icon: <NavigationItemIcon id='guest-manager' />,
      },
      ...(obeeEnabled
        ? [
            {
              id: 'tags',
              name: 'Tags',
              slug: 'segmentation-tags',
              column: 'demand',
              section: 1,
              allowedScopes: FEATURE_SCOPES.BOOKINGS,
              staffOnly: true,
              icon: <NavigationItemIcon id='tags' />,
            },
          ]
        : []),
      {
        id: 'transactions',
        name: 'Transactions',
        slug: 'transactions',
        column: 'demand',
        section: 1,
        allowedScopes: FEATURE_SCOPES.TRANSACTIONS,
        icon: <NavigationItemIcon id='transactions' />,
      },
      {
        id: 'billing-and-settlement',
        name: 'Billing and Settlement',
        slug: billingSlug,
        column: 'settings',
        section: 1,
        allowedScopes: FEATURE_SCOPES.SETTLEMENTS,
        icon: <NavigationItemIcon id='billing-and-settlement' />,
      },
      {
        id: 'menus',
        name: 'Menus',
        slug: 'menus',
        column: 'settings',
        section: 1,
        allowedScopes: FEATURE_SCOPES.MENUS,
        icon: <NavigationItemIcon id='menus' />,
      },
      {
        id: 'integrations',
        name: 'Integrations',
        slug: 'pos-integration',
        column: 'settings',
        section: 1,
        icon: <NavigationItemIcon id='integrations' />,
      },
      // {
      //   id: 'partners',
      //   name: 'Partners',
      //   slug: '',
      //   column: 'settings',
      //   section: 1,
      //   icon: <NavigationItemIcon id='partners' />,
      // },
      {
        id: 'help-centre',
        name: 'Help centre',
        slug: 'help-centre',
        column: 'settings',
        section: 1,
        allowedScopes: FEATURE_SCOPES.HELP_CENTRE,
        icon: <NavigationItemIcon id='help-centre' />,
      },
    ];

    if (platform === 'whitelabel') {
      const dealIndex = newNavigationItems.findIndex((nav) => nav.name === 'Goals');
      if (dealIndex >= 0) {
        newNavigationItems.splice(dealIndex, 1);
      }
    } else if (goalsEnabled === false) {
      const dealIndex = newNavigationItems.findIndex((nav) => nav.name === 'Offers');
      if (dealIndex >= 0) {
        newNavigationItems[dealIndex] = {
          id: 'deals',
          name: 'Deals',
          slug: 'deals',
          section: 1,
          column: 'demand',
          icon: <NavigationItemIcon id='deals' />,
        };
      }
    }

    const slugBlocked = (slug) => {
      if (slug === null || slug === undefined || slug === '') {
        return false;
      }
      const path = `/${String(slug)}`.replace(/\/+/g, '/');
      return appConfig.blockedRoutes.includes(path);
    };

    return newNavigationItems.filter((item) => !slugBlocked(item.slug));
  };

  const [navigationItems, setNavigationItems] = useState(getNavigationItems());
  const [showDrawer, setShowDrawer] = useState(false); // temporary nav and tablet/mobile nav

  const contextValue = useMemo(
    () => ({ showNavigation: showDrawer, setShowNavigation: setShowDrawer }),
    [showDrawer, setShowDrawer],
  );

  const route = routes.find((routeToFind) => matchPath(location.pathname, routeToFind));
  const previousPathRef = useRef(location.pathname);
  const navigatingAwayFromBookings =
    previousPathRef.current.startsWith('/livebookings') &&
    !location.pathname.startsWith('/livebookings');

  useEffect(() => {
    setNavigationItems(getNavigationItems());
  }, [
    goalsEnabled,
    obeeEnabled,
    ecPayEnabled,
    premium,
    forecasterEnabled,
    analyticsEnabled,
    platform,
  ]);

  // Set title and header
  useEffect(() => {
    const pageTitle = route?.pageTitle || 'Dashboard';
    const titleDelimiter = pageTitle !== '' ? ' | ' : '';
    const restaurantName = restaurantActive?.restaurant?.name;
    const titleEnd = `${import.meta.env.VITE_WEBSITE_NAME} Partners`;
    // window.document.title = `${pageTitle}${titleDelimiter}${titleEnd}`;
    window.document.title = [pageTitle, restaurantName, titleEnd]
      ?.filter((i) => !!i)
      ?.join(titleDelimiter);
  }, [location.pathname, navigationItems, restaurantActive?.restaurant?.name]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0); // start at the top of the page for each navigation
  }, [location.pathname]);

  useEffect(() => {
    previousPathRef.current = location.pathname;
  }, [location.pathname]);

  const handleShowDrawer = () => {
    setShowDrawer(true);
  };

  const hideDrawer = () => {
    setShowDrawer(false);
  };

  const restaurant = restaurantActive?.restaurant;
  const venueInactiveForPromoModals = isVenueInactiveForPromoModals(restaurant);

  // Whether the restaurant has loaded and is considered disabled
  const restaurantIsDisabled = isRestaurantDisabled(restaurant);

  // Whether to block this route because venue is disabled (and not new)
  const routeIsDisabled = restaurantIsDisabled && !canEditAll(user) && !route?.allowIfDisabled;

  const breakpointMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const allowHideNavigation =
    !routeIsDisabled &&
    (route?.hideNavigation ||
      (route?.header === 'obee' &&
        ((route?.premiumRequired && premium) || !route?.premiumRequired))); // Whether this screen lets the side bar collapse

  // Logic for whether we are showing the navigation
  // prettier-ignore
  const isOpenDesktop =
    routeIsDisabled ? true // Never hide navigation if we're on a disabled route because we can't bring it back
    : route?.forceHideNavigation ? false // Route is special and does not allow any navigation
      : allowHideNavigation
        ? showDrawer // Defer to drawer logic if nav is hideable
        : true; // Ensure we always show the sidebar on non-obee screens

  // TODO this is a better method of checking initial load of restaurant:
  const loadingRestaurant =
    restaurantsFetching || restaurantActive.restaurantFetching || restaurantActive.fetching;

  // Force it to show if a staff account has not selected a restaurant
  const [forceShow, setForceShow] = useState(false);

  // Also force show the navigation if the user does not have permission to view the current page
  const noPermission = route?.allowedScopes && !hasAccess(user, route?.allowedScopes);

  // Ensure that if a staff user has not selected a restaurant, we show the restaurant selector no matter what
  useEffect(() => {
    if (!loadingRestaurant && (noPermission || isEmpty(restaurantActive.restaurant))) {
      setForceShow(true);
    } else {
      setForceShow(false);
    }
  }, [restaurantActive, noPermission, routeIsDisabled]);

  const isOpen = (breakpointMdDown ? showDrawer : isOpenDesktop) || forceShow; // force open if no restaurant is active for staff logins

  // Audience targeting modal - show on insights and analytics (and other key pages)
  const [showAudienceTargetingModal, setShowAudienceTargetingModal] = useState(false);
  const AUDIENCE_TARGETING_MODAL_PATHS = [
    '/insights',
    '/insights/customers',
    '/analytics',
    '/customer-value',
  ];
  const isOnAudienceTargetingModalPage = AUDIENCE_TARGETING_MODAL_PATHS.some((path) =>
    matchPath(location.pathname, { path, exact: true }),
  );
  const hasAudienceTargetingAccess = hasAccess(user, FEATURE_SCOPES.AUDIENCE_TARGETING);

  useEffect(() => {
    if (restId && hasAudienceTargetingAccess) {
      fetchAudienceTargeting();
    }
  }, [restId, hasAudienceTargetingAccess, fetchAudienceTargeting]);

  useEffect(() => {
    if (!hasAudienceTargetingAccess || !restId || !isOnAudienceTargetingModalPage) return;

    if (venueInactiveForPromoModals) {
      setShowAudienceTargetingModal(false);
      return;
    }

    const hasSeen = localStorage.getItem(HAS_SEEN_AUDIENCE_TARGETING_MODAL) === 'true';
    const isTargetingEnabled = audienceTargeting?.data?.enabled;

    if (hasSeen || isTargetingEnabled) {
      setShowAudienceTargetingModal(false);
      return;
    }

    setShowAudienceTargetingModal(true);
  }, [
    audienceTargeting?.data?.enabled,
    hasAudienceTargetingAccess,
    restId,
    isOnAudienceTargetingModalPage,
    venueInactiveForPromoModals,
    restaurant?.enabled,
    restaurant?.paused,
    restaurant?.closurePause,
  ]);

  const handleAudienceTargetingModalClose = () => {
    localStorage.setItem(HAS_SEEN_AUDIENCE_TARGETING_MODAL, 'true');
    setShowAudienceTargetingModal(false);
  };

  const [isHolidayHoursModalOpen, setIsHolidayHoursModalOpen] = useState(false);
  const [isLoyaltyModalOpen, setIsLoyaltyModalOpen] = useState(false);

  // Holiday hours modal — trigger from AppLayout so it fires on all routes (including forecasting header)
  useEffect(() => {
    const restaurant = restaurantActive?.restaurant;
    const restaurantConfig = getRestaurantConfig(restaurant);
    const profile = getHolidayHoursProfileForRestaurant(restaurant, restaurantConfig.holidayHours);
    const hdsModalStartDate = profile?.hdsModalStartDate;
    const hdsModalEndDate = profile?.hdsModalEndDate ?? profile?.rangeEnd;

    if (
      !isHolidayHoursSuppressedByConfig(restaurantConfig.holidayHours) &&
      profile &&
      !venueInactiveForPromoModals &&
      !holidayScheduleStatus.fetching &&
      holidayScheduleStatus.success &&
      !holidayScheduleStatus.data?.holidaysScheduled &&
      hdsModalEndDate &&
      (!hdsModalStartDate || !dayjs().isBefore(hdsModalStartDate, 'day')) &&
      !dayjs().isAfter(hdsModalEndDate, 'day')
    ) {
      setIsHolidayHoursModalOpen(true);
      return;
    }
    setIsHolidayHoursModalOpen(false);
  }, [
    holidayScheduleStatus,
    restaurantActive,
    venueInactiveForPromoModals,
    restaurant?.enabled,
    restaurant?.paused,
    restaurant?.closurePause,
  ]);

  const partnerPromoModalsValue = useMemo(
    () => ({
      holidayHoursModalVisible:
        isHolidayHoursModalOpen && !venueInactiveForPromoModals && !isLoyaltyModalOpen,
      setHolidayHoursModalVisible: () => {},
      isLoyaltyModalOpen,
      setIsLoyaltyModalOpen,
    }),
    [isHolidayHoursModalOpen, isLoyaltyModalOpen, venueInactiveForPromoModals],
  );

  const renderHeader = () => {
    // If restaurant is not enabled, don't show the page (admins can still access)
    if (routeIsDisabled) {
      if (restaurantActive?.restaurant?.isNew) {
        return (
          <AppHeader showDrawer={handleShowDrawer} forceShowHeader>
            <Box>
              <p>
                Your account is being set up. Our team will contact you when your venue is ready to
                go live.
              </p>
              <br />
              <p>
                Please contact support via{' '}
                <a href={`mailto:${appConfig.supportEmail2}`} target='_blank' rel='noreferrer'>
                  {appConfig.supportEmail2}
                </a>{' '}
                if you have any questions.
              </p>
            </Box>
          </AppHeader>
        );
      }

      return (
        <AppHeader showDrawer={handleShowDrawer} forceShowHeader>
          <Box>
            This account is currently disabled. Please contact support via{' '}
            <a href={`mailto:${appConfig.supportEmail2}`} target='_blank' rel='noreferrer'>
              {appConfig.supportEmail2}
            </a>{' '}
            if you wish to have the account re-enabled.
          </Box>
        </AppHeader>
      );
    }

    if (!((route?.premiumRequired && premium) || !route?.premiumRequired)) {
      return (
        <AppHeader showDrawer={handleShowDrawer}>
          <>{children}</>
        </AppHeader>
      );
    }

    // TODO replace this with something else, but ideally don't render ec bookings until a rest ID is set
    // TODO also handle if you're staff and no restaurant is set
    // TODO can optimize further if restaurant id is stored in local state
    if (!restId) {
      return <Box />;
    }

    // Redirect if user does not have the valid permissions to view this page
    if (noPermission) {
      devLog('info', 'You cannot access this screen');

      return (
        <AppHeader showDrawer={handleShowDrawer} forceShowHeader>
          <Box>You do not have access to view this screen</Box>
        </AppHeader>
      );
    }

    switch (route?.header) {
      case 'obee':
        return (
          <LiveBookingsHeader
            showNavigation={handleShowDrawer}
            navigationOpen={isOpen}
            pageId={route?.id}
          >
            {children}
          </LiveBookingsHeader>
        );
      case 'forecasting':
        return (
          <NavigationContext.Provider value={contextValue}>
            <ForecastingHeader
              navigationOpen={isOpen}
              disableDrawerMargin={route?.disableDrawerMargin}
              disableDrawerMarginOnMdDown={route?.disableDrawerMarginOnMdDown}
              showMobileHeader={route?.showMobileHeader}
              showNavigation={handleShowDrawer}
            >
              {children}
            </ForecastingHeader>
          </NavigationContext.Provider>
        );
      case 'custom':
        // Put the navigation controls in context so the custom component can use them
        return (
          <NavigationContext.Provider value={contextValue}>
            <CustomHeader showNavigation={handleShowDrawer} navigationOpen={isOpen}>
              {children}
            </CustomHeader>
          </NavigationContext.Provider>
        );
      default:
        return (
          <AppHeader showDrawer={handleShowDrawer}>
            <>{children}</>
          </AppHeader>
        );
    }
  };

  return (
    <PartnerPromoModalsContext.Provider value={partnerPromoModalsValue}>
      {/* DevTools: filter console by "[HolidayHoursNav]" to debug holiday-hours leave guard */}
      <HolidayHoursNavigationGuardProvider>
        {restId && hasAudienceTargetingAccess && (
          <AudienceTargetingModal
            isOpen={
              showAudienceTargetingModal &&
              !venueInactiveForPromoModals &&
              !isHolidayHoursModalOpen &&
              !isLoyaltyModalOpen
            }
            onClose={handleAudienceTargetingModalClose}
            audienceTargeting={audienceTargeting}
          />
        )}
        <HolidayHoursModal
          isOpen={isHolidayHoursModalOpen && !venueInactiveForPromoModals && !isLoyaltyModalOpen}
          onClose={() => setIsHolidayHoursModalOpen(false)}
          onSave={() => setIsHolidayHoursModalOpen(false)}
        />
        <nav className={classes.drawer}>
          <Drawer
            variant={
              !forceShow && (breakpointMdDown || allowHideNavigation) ? 'temporary' : 'persistent'
            }
            transitionDuration={navigatingAwayFromBookings ? { enter: 0, exit: 0 } : undefined}
            open={isOpen}
            onClose={hideDrawer}
            classes={{
              paper: classes.drawerPaper,
            }}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile
              onBackdropClick: hideDrawer,
            }}
          >
            <NavigationDrawer
              hideDrawer={hideDrawer}
              navigationItems={navigationItems}
              allowHideNavigation={allowHideNavigation}
            />
          </Drawer>
        </nav>
        {restId && renderHeader()}
      </HolidayHoursNavigationGuardProvider>
    </PartnerPromoModalsContext.Provider>
  );
};

AppLayout.propTypes = {
  platform: PropTypes.string,
  goalsEnabled: PropTypes.bool,
  obeeEnabled: PropTypes.bool,
  premium: PropTypes.bool,
  forecasterEnabled: PropTypes.bool,
  analyticsEnabled: PropTypes.bool,
  ecPayEnabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
  restId: PropTypes.string,
  restaurantActive: restaurantActivePropTypes.isRequired,
  restaurantsFetching: PropTypes.bool,
  user: PropTypes.shape({}).isRequired,
  holidayScheduleStatus: PropTypes.shape({}),
};

const mapStateToProps = (state) => ({
  platform: state.restaurantActive.restaurant?.platform,
  goalsEnabled: state.restaurantActive.restaurant?.offersEnabled,
  obeeEnabled: state.restaurantActive.restaurant?.obeeEnabled,
  forecasterEnabled: state.forecasterEnabled.forecasterEnabled,
  analyticsEnabled: state.forecasterEnabled.analyticsEnabled,
  restaurantActive: state.restaurantActive,
  restaurantsFetching: state.restaurants.fetching,
  premium: state.restaurantActive.restaurant?.premium,
  ecPayEnabled: state.restaurantActive.restaurant?.ecPayEnabled,
  restId: state.restaurantActive.restaurant.objectId,
  user: state.user,
  audienceTargeting: state.audienceTargeting,
  holidayScheduleStatus: state.holidayScheduleStatus,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchAudienceTargeting: fetchAudienceTargetingAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(AppLayout);
